import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { products as mockProducts } from '../mock/mock';
import { fetchProduct } from '../lib/api';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { useRecentlyViewed } from '../hooks/useRecentlyViewed';
import { Heart, ShoppingBag, Minus, Plus, Truck, Sparkles, RefreshCw, Star, Share2, ChevronRight, Play, Package, Download } from 'lucide-react';
import RelatedProducts from '../components/RelatedProducts';
import RecentlyViewed from '../components/RecentlyViewed';
import { toast } from 'sonner';

const normalize = (p) => ({ ...p, originalPrice: p.original_price ?? p.originalPrice, hoverImage: p.hover_image ?? p.hoverImage });

const youtubeEmbed = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart, toggleWishlist, inWishlist } = useCart();
  const { format } = useCurrency();
  const { track } = useRecentlyViewed();
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [tab, setTab] = useState('details');
  const [customName, setCustomName] = useState('');
  const [selectedOptions, setSelectedOptions] = useState({});
  const [videoOpen, setVideoOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchProduct(id).then(({ product, related }) => {
      const norm = normalize(product);
      setProduct(norm);
      setRelated((related || []).map(normalize));
      setLoading(false);
      track(norm);
      // Auto-select first variant options
      if (product?.variant_options?.length) {
        const init = {};
        product.variant_options.forEach(opt => { init[opt.name] = opt.values[0]; });
        setSelectedOptions(init);
      } else {
        setSelectedOptions({});
      }
    }).catch(() => {
      const fallback = mockProducts.find(p => p.id === id);
      if (fallback) { setProduct(fallback); track(fallback); }
      setRelated([]);
      setLoading(false);
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setActiveImg(0); setQty(1); setCustomName('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Compute active variant based on selected options
  const activeVariant = useMemo(() => {
    if (!product?.variants?.length) return null;
    return product.variants.find(v => {
      const attrs = v.attrs || {};
      return Object.keys(selectedOptions).every(k => attrs[k] === selectedOptions[k]);
    }) || null;
  }, [product, selectedOptions]);

  if (loading) return <main className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 border-4 border-[#D4A63A] border-t-transparent rounded-full animate-spin" /></main>;

  if (!product) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="font-serif-display text-4xl mb-3">Product not found</div>
          <button onClick={() => navigate('/shop')} className="text-sm underline">Back to shop</button>
        </div>
      </main>
    );
  }

  const gallery = [product.image, product.hoverImage, ...(product.gallery || [])].filter((v, i, a) => v && a.indexOf(v) === i);
  const videos = product.videos || [];
  const wished = inWishlist(product.id);
  const displayPrice = activeVariant?.price ?? product.price;
  const displayOriginal = activeVariant?.original_price ?? product.originalPrice;
  const displaySku = activeVariant?.sku || product.sku || `WTH-${product.id.toUpperCase()}`;
  const displayStock = activeVariant?.stock ?? product.stock ?? 0;
  const inStock = displayStock > 0;

  const handleAdd = () => {
    if (product.variants?.length && !activeVariant) { toast.error('Please select all options'); return; }
    if (!inStock) { toast.error('Out of stock'); return; }
    const variantLabel = activeVariant ? Object.entries(activeVariant.attrs || {}).map(([k,v]) => `${k}: ${v}`).join(', ') : null;
    addToCart({
      ...product,
      variant_id: activeVariant?.id,
      variant_label: variantLabel,
      price: displayPrice,
      image: activeVariant?.image || product.image,
      customization: customName || undefined,
    }, qty);
    toast.success(`Added ${qty} × ${product.name}${variantLabel ? ' (' + variantLabel + ')' : ''} to bag`);
  };

  const productJsonLd = {
    '@context': 'https://schema.org', '@type': 'Product',
    'name': product.name,
    'image': gallery,
    'description': product.description,
    'sku': displaySku,
    'brand': { '@type': 'Brand', 'name': 'Wear The Hope with Ben' },
    'offers': {
      '@type': 'Offer', 'price': displayPrice, 'priceCurrency': 'USD',
      'availability': inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
    },
    'aggregateRating': { '@type': 'AggregateRating', 'ratingValue': product.rating, 'reviewCount': product.reviews },
  };

  return (
    <main className="pt-6 pb-24">
      <Helmet>
        <title>{product.seo_title || `${product.name} | Wear The Hope with Ben`}</title>
        <meta name="description" content={product.seo_description || product.description?.slice(0, 160)} />
        {product.seo_keywords && <meta name="keywords" content={product.seo_keywords} />}
        <meta property="og:type" content="product" />
        <meta property="og:title" content={product.name} />
        <meta property="og:description" content={product.description} />
        <meta property="og:image" content={product.image} />
        <script type="application/ld+json">{JSON.stringify(productJsonLd)}</script>
      </Helmet>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <nav className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-8">
          <Link to="/" className="hover:text-[#8A6A22]">Home</Link>
          <ChevronRight className="inline w-3 h-3 mx-2" />
          <Link to="/shop" className="hover:text-[#8A6A22]">Shop</Link>
          {product.category_slug && (<><ChevronRight className="inline w-3 h-3 mx-2" /><Link to={`/shop/${product.category_slug}`} className="hover:text-[#8A6A22]">{product.category_name}</Link></>)}
          <ChevronRight className="inline w-3 h-3 mx-2" />
          <span className="text-[#8A6A22]">{product.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="flex gap-4">
            <div className="hidden md:flex flex-col gap-3 w-20 shrink-0">
              {gallery.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${activeImg === i ? 'border-[#D4A63A]' : 'border-transparent'}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
              {videos.map((v, i) => (
                <button key={'v'+i} onClick={() => setVideoOpen(v)} className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-[#D4A63A] bg-black relative">
                  <Play className="w-6 h-6 text-white absolute inset-0 m-auto" />
                  <div className="absolute inset-0 flex items-center justify-center text-[10px] text-white/70 uppercase">Video</div>
                </button>
              ))}
            </div>
            <div className="flex-1 aspect-[4/5] rounded-2xl overflow-hidden bg-white border border-[#E7DCC7] relative">
              <img src={gallery[activeImg]} alt={product.name} className="w-full h-full object-cover transition-opacity duration-500" />
              {product.ribbon && (
                <span className="absolute top-4 left-4 shimmer-badge text-white text-xs uppercase tracking-[0.15em] px-4 py-1.5 rounded-full">{product.ribbon}</span>
              )}
              {videos.length > 0 && (
                <button onClick={() => setVideoOpen(videos[0])} className="absolute bottom-4 right-4 w-12 h-12 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-lg hover:bg-[#D4A63A] hover:text-white transition-colors group" aria-label="Play video">
                  <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
                </button>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {product.ribbon && <span className="shimmer-badge text-white text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full">{product.ribbon}</span>}
              {product.product_type === 'digital' && <span className="bg-emerald-600 text-white text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full inline-flex items-center gap-1"><Download className="w-3 h-3" /> Digital</span>}
              <span className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">SKU: {displaySku}</span>
            </div>
            <h1 className="font-serif-display text-4xl md:text-5xl text-[#213A60] leading-tight">{product.name}</h1>
            {product.verse && <div className="italic text-neutral-600 mt-2">{product.verse}</div>}

            <div className="flex items-center gap-3 mt-4">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < Math.round(product.rating) ? 'fill-[#E8B364] stroke-[#E8B364]' : 'stroke-neutral-300'}`} />)}
              </div>
              <span className="text-sm text-neutral-600">{product.rating} ({product.reviews} reviews)</span>
              <span className={`ml-auto text-[11px] uppercase tracking-[0.15em] px-3 py-1 rounded-full ${inStock ? 'text-emerald-700 bg-emerald-50' : 'text-red-700 bg-red-50'}`}>{inStock ? `In stock (${displayStock})` : 'Out of stock'}</span>
            </div>

            <div className="flex items-baseline gap-3 mt-6">
              <div className="text-3xl font-serif-display text-[#8A6A22]" data-testid="product-price">{format(displayPrice)}</div>
              {displayOriginal > displayPrice && (
                <>
                  <div className="text-lg text-neutral-400 line-through">{format(displayOriginal)}</div>
                  <div className="text-xs uppercase tracking-widest text-[#8A6A22]">Save {Math.round(((displayOriginal - displayPrice) / displayOriginal) * 100)}%</div>
                </>
              )}
            </div>
            <div className="text-xs text-neutral-500 mt-1">Inclusive of all taxes</div>

            <p className="mt-6 text-neutral-700 leading-relaxed">{product.description}</p>

            {/* Variant options */}
            {product.variant_options?.map(opt => (
              <div key={opt.name} className="mt-6">
                <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-600 mb-2">{opt.name}: <span className="text-[#8A6A22]">{selectedOptions[opt.name]}</span></div>
                <div className="flex flex-wrap gap-2">
                  {opt.values.map(val => {
                    const isSelected = selectedOptions[opt.name] === val;
                    return (
                      <button
                        key={val}
                        onClick={() => setSelectedOptions(prev => ({ ...prev, [opt.name]: val }))}
                        className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider border transition-colors ${isSelected ? 'border-[#D4A63A] bg-[#D4A63A] text-[#213A60] font-semibold' : 'border-[#E7DCC7] bg-white hover:border-[#D4A63A]'}`}
                      >{val}</button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Static colors (if no variants) */}
            {(!product.variant_options?.length && product.colors?.length > 0) && (
              <div className="mt-6">
                <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-600 mb-2">Color</div>
                <div className="flex gap-2">
                  {product.colors.map((c, i) => (
                    <button key={i} className="w-9 h-9 rounded-full border-2 border-white ring-1 ring-[#E7DCC7] hover:ring-[#D4A63A] transition-colors" style={{ background: c }} aria-label="Color" />
                  ))}
                </div>
              </div>
            )}

            {product.personalization_enabled && (
              <div className="mt-6 bg-[#F8F4EC] border border-[#E7DCC7] rounded-xl p-5">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8A6A22] mb-3">
                  <Sparkles className="w-3.5 h-3.5" /> {product.personalization_label || 'Personalize (optional)'}
                </div>
                <input
                  value={customName}
                  onChange={e => setCustomName(e.target.value.slice(0, product.personalization_max_length || 40))}
                  placeholder="Type name or short verse to engrave"
                  className="w-full bg-white border border-[#E7DCC7] rounded-full px-5 py-3 text-sm outline-none focus:border-[#D4A63A] transition-colors"
                />
                <div className="text-[11px] text-neutral-500 mt-2 flex justify-between">
                  <span>Free customization · Adds 2-3 days to delivery</span>
                  <span>{customName.length}/{product.personalization_max_length || 40}</span>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 mt-8">
              <div className="inline-flex items-center border border-[#E7DCC7] rounded-full bg-white">
                <button onClick={() => setQty(q => Math.max(1, q - 1))} className="w-11 h-12 flex items-center justify-center hover:bg-[#F8F4EC] rounded-l-full"><Minus className="w-4 h-4" /></button>
                <span className="w-12 text-center">{qty}</span>
                <button onClick={() => setQty(q => Math.min(displayStock || 99, q + 1))} className="w-11 h-12 flex items-center justify-center hover:bg-[#F8F4EC] rounded-r-full"><Plus className="w-4 h-4" /></button>
              </div>
              <button disabled={!inStock} onClick={handleAdd} className="flex-1 bg-[#213A60] hover:bg-[#D4A63A] disabled:bg-neutral-300 disabled:cursor-not-allowed transition-colors text-white py-4 rounded-full text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2">
                <ShoppingBag className="w-4 h-4" /> {inStock ? 'Add to Bag' : 'Out of Stock'}
              </button>
              <button onClick={() => toggleWishlist(product)} className={`w-12 h-12 rounded-full border border-[#E7DCC7] flex items-center justify-center hover:border-[#D4A63A] transition-colors ${wished ? 'text-[#8A6A22]' : ''}`} aria-label="Wishlist">
                <Heart className={`w-4 h-4 ${wished ? 'fill-current' : ''}`} />
              </button>
              <button onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Link copied'); }} className="w-12 h-12 rounded-full border border-[#E7DCC7] flex items-center justify-center hover:border-[#D4A63A] transition-colors" aria-label="Share"><Share2 className="w-4 h-4" /></button>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-8 pt-6 border-t border-[#E7DCC7]">
              {product.product_type === 'digital' ? [
                { icon: Download, l: 'Instant Access', s: 'Delivered by email' },
                { icon: RefreshCw, l: 'Lifetime Access', s: 'Re-download anytime' },
                { icon: Sparkles, l: 'Best Quality', s: 'High-res files' },
              ] : [
                { icon: Truck, l: 'Free Shipping', s: 'On US orders $500+' },
                { icon: RefreshCw, l: 'Easy Returns', s: '30-day window' },
                { icon: Package, l: 'Gift Wrapped', s: 'Ready to give' },
              ].map((a, i) => {
                const I = a.icon;
                return (
                  <div key={i} className="text-center">
                    <I className="w-5 h-5 text-[#8A6A22] mx-auto mb-2" />
                    <div className="text-xs font-medium">{a.l}</div>
                    <div className="text-[10px] text-neutral-500">{a.s}</div>
                  </div>
                );
              })}
            </div>

            <div className="mt-10 border-t border-[#E7DCC7] pt-6">
              <div className="flex gap-6 text-xs uppercase tracking-[0.2em]">
                {['details', 'shipping', 'reviews'].map(t => (
                  <button key={t} onClick={() => setTab(t)} className={`pb-3 border-b-2 transition-colors ${tab === t ? 'border-[#D4A63A] text-[#8A6A22]' : 'border-transparent text-neutral-500'}`}>{t}</button>
                ))}
              </div>
              <div className="pt-5 text-sm text-neutral-700 leading-relaxed">
                {tab === 'details' && <p>{product.description || 'Made with the finest materials and handcrafted attention to detail.'}</p>}
                {tab === 'shipping' && <p>{product.product_type === 'digital' ? 'This is a digital product. You will receive a download link on your email within minutes of purchase.' : 'We ship across the US in 3-6 business days (Standard) or 1-2 days (Express). Canada shipping in 5-10 business days. Free standard shipping on US orders over $500.'}</p>}
                {tab === 'reviews' && <p>Rated {product.rating}/5 across {product.reviews} customer reviews. Verified buyers love the quality and thoughtful presentation.</p>}
              </div>
            </div>
          </div>
        </div>

        {related.length === 0 ? (
          <RelatedProducts productId={product.id} limit={8} />
        ) : (
          <RelatedProducts productId={product.id} limit={8} />
        )}

        <RecentlyViewed excludeId={product.id} />
      </div>

      {/* Video modal */}
      {videoOpen && (
        <>
          <div onClick={() => setVideoOpen(false)} className="fixed inset-0 bg-black/80 z-50" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
            <div className="w-full max-w-4xl aspect-video bg-black rounded-2xl overflow-hidden">
              {youtubeEmbed(videoOpen) ? (
                <iframe src={youtubeEmbed(videoOpen) + '?autoplay=1'} title="Product video" allowFullScreen allow="autoplay" className="w-full h-full" />
              ) : (
                <video src={videoOpen} controls autoPlay className="w-full h-full" />
              )}
            </div>
          </div>
        </>
      )}
    </main>
  );
};

export default ProductDetail;
